<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { CampaignService } from '@/services/campaign.services';
import ProductEditTabs from '@/components/product/edit/ProductEditTabs.vue';
import CollapsibleSection from '@/components/product/edit/CollapsibleSection.vue';
import CampaignListPanel from './CampaignListPanel.vue';
import CampaignFormPanel from './CampaignFormPanel.vue';
import CampaignTargetsPanel from './CampaignTargetsPanel.vue';
import CampaignMediaPanel from './CampaignMediaPanel.vue';
import type { ICampaign } from './campaign.types';

const props = defineProps<{ storeId: string }>();

type ViewMode = 'list' | 'edit';
type CampaignTabKey = 'general' | 'targets' | 'media';

const viewMode = ref<ViewMode>('list');
const selectedCampaignId = ref<string | null>(null);
const activeTab = ref<CampaignTabKey>('general');

const TABS = [
  { key: 'general', label: 'Général' },
  { key: 'targets', label: 'Ciblage' },
  { key: 'media', label: 'Médias' },
];

function openCampaign(id: string) {
  selectedCampaignId.value = id;
  activeTab.value = 'general';
  viewMode.value = 'edit';
}

function backToList() {
  viewMode.value = 'list';
  selectedCampaignId.value = null;
}

const { data: campaign } = useQuery({
  queryKey: computed(() => [
    'campaign-detail',
    props.storeId,
    selectedCampaignId.value,
  ]),
  queryFn: () =>
    CampaignService.getById(props.storeId, selectedCampaignId.value as string),
  enabled: computed(
    () => viewMode.value === 'edit' && !!selectedCampaignId.value,
  ),
});

function onFormSaved(saved: ICampaign) {
  selectedCampaignId.value = saved.id;
}
</script>

<template>
  <div class="cct">
    <CampaignListPanel
      v-if="viewMode === 'list'"
      :store-id="storeId"
      @edit-campaign="openCampaign"
    />

    <template v-else>
      <button type="button" class="cct__back" @click="backToList">
        <i class="pi pi-arrow-left" /> Retour à la liste
      </button>

      <ProductEditTabs v-model="activeTab" :tabs="TABS" />

      <div class="cct__tab-panel">
        <CollapsibleSection
          v-if="activeTab === 'general'"
          section-key="campaign-general"
          icon="pi-info-circle"
          title="Informations générales"
          default-open
        >
          <CampaignFormPanel
            :store-id="storeId"
            :campaign-id="selectedCampaignId ?? undefined"
            @saved="onFormSaved"
            @cancel="backToList"
          />
        </CollapsibleSection>

        <template v-if="selectedCampaignId && campaign">
          <CollapsibleSection
            v-if="activeTab === 'targets'"
            section-key="campaign-targets"
            icon="pi-bullseye"
            title="Ciblage"
            subtitle="Produits, catégories et promotions concernés par la campagne"
            default-open
          >
            <CampaignTargetsPanel
              :store-id="storeId"
              :campaign-id="selectedCampaignId"
              :initial-targets="campaign.targets"
            />
          </CollapsibleSection>

          <CollapsibleSection
            v-if="activeTab === 'media'"
            section-key="campaign-media"
            icon="pi-images"
            title="Médias"
            subtitle="Bannière, vignette et galerie de la campagne"
            default-open
          >
            <CampaignMediaPanel
              :store-id="storeId"
              :campaign-id="selectedCampaignId"
            />
          </CollapsibleSection>
        </template>

        <div v-else-if="activeTab !== 'general'" class="cct__locked">
          <i class="pi pi-lock" />
          <p>Enregistrez d'abord les informations générales de la campagne.</p>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.cct {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cct__back {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  font-family: inherit;
}
.cct__tab-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cct__locked {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.cct__locked i {
  font-size: 2rem;
  opacity: 0.4;
}
</style>
